<script lang="ts" setup>

//vue
import { onMounted, reactive, watch } from "vue";

//servicos
import * as service from "@/services/planosItensService";
import { f2dec } from "@/utils/funcoes";
import { useAcl } from "vue-simple-acl";
const acl = useAcl();

//componentes
import TrataTecla from "@/components/TrataTecla/Index.vue";
import MsgBox from "@/components/MsgBox/Index.vue";
import TextBox from "@/components/TextBox/Index.vue";
import DropdownSimNao from "@/components/Dropdowns/DropdownSimNao.vue";

interface IProps {
  PlanoId?: number
}

var props = defineProps<IProps>();

function modeloVazio(): service.PlanoItem {
  return {
    planoItemId: 0,
    planoId: props.PlanoId ?? 0,
    descricao: "",
    idadeMinima: null,
    idadeMaxima: null,
    valor: 0,
    comSeguro: false,
    valorComSeguro: 0,
    ativo: true,
  } as service.PlanoItem;
}

var controle = reactive({
  planoId: props.PlanoId as number | null | undefined,
  itens: [] as service.PlanoItem[],
  model: modeloVazio(),
  janela: false,
  janPergunta: false,
  erro: "",
  status: ""
})

onMounted(() => {
  buscarItens();
});

watch(() => props.PlanoId, (newValue) => {
  controle.planoId = newValue;
  buscarItens();
});

async function buscarItens() {

  if (!props.PlanoId) {
    controle.itens = [];
    return;
  }

  controle.status = "Buscando items...";
  controle.erro = "";
  service.obter({ planoId: props.PlanoId } as service.Filtros)
    .then((itens) => {
      controle.itens = itens;
      controle.status = "";
    })
    .catch((error) => {
      controle.erro = `Erro ao buscar items: ${error}`;
      controle.status = "";
    });
}

async function salvar() {

  controle.status = "Salvando item...";
  controle.erro = "";

  controle.model.planoId = props.PlanoId ?? 0;

  service.salvar(controle.model)
    .then(() => {
      controle.status = "";
      controle.model = modeloVazio();
      buscarItens();
      controle.janela = false;
    })
    .catch((error) => {
      controle.erro = `Erro ao salvar item: ${error}`;
      controle.status = "";
    });
}

function excluir(item: service.PlanoItem) {
  controle.model = item;
  controle.janPergunta = true;
}

function excluirConfirma() {
  controle.janPergunta = false;
  controle.status = "Excluindo item...";
  controle.erro = "";

  service.excluir(controle.model.planoItemId)
    .then(() => {
      controle.status = "";
      controle.model = modeloVazio();
      buscarItens();
    })
    .catch((error) => {
      controle.erro = `Erro ao excluir item: ${error}`;
      controle.status = "";
    });
}

</script>

<template>
  <TrataTecla>
    <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
      <div class="card-header">
        <h5 class="card-title">Itens Adicionais</h5>
      </div>
      <div class="card-body p-3" v-if="!props.PlanoId">
        <div class="row mb-2">
          <div class="col-md-12">
            <p class="">Necessário cadastro do plano</p>
          </div>
        </div>
      </div>

      <div class="card-body p-3" v-else>
        <div class="row mb-2">
          <div class="col-md-6">
            <a class="btn btn-sm btn-primary " @click="controle.model = modeloVazio(); controle.janela = true">Adicionar</a>
          </div>
        </div>

        <div class="row mb-2">
          <div class="col-md-12">
            <table class="table table-sm table-striped table-bordered table-hover">
              <thead>
                <tr>
                  <th>Descrição</th>
                  <th class="text-center">Idade Mínima</th>
                  <th class="text-center">Idade Máxima</th>
                  <th class="text-end">Valor R$</th>
                  <th class="text-center">Com Seguro?</th>
                  <th class="text-end">Valor Com Seguro R$</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in controle.itens" :key="index">
                  <td>{{ item.descricao }}</td>
                  <td class="text-center">{{ item.idadeMinima ?? '-' }}</td>
                  <td class="text-center">{{ item.idadeMaxima ?? '-' }}</td>
                  <td class="text-end">{{ f2dec(item.valor) }}</td>
                  <td class="text-center">{{ item.comSeguro ? 'Sim' : 'Não' }}</td>
                  <td class="text-end">{{ item.comSeguro ? f2dec(item.valorComSeguro) : '-' }}</td>
                  <td>
                    <button class="btn btn-sm btn-primary me-1" title="Alterar registro" @click="controle.model = item; controle.janela = true">
                      <i class="flaticon-edit"></i>
                    </button>

                    <button class="btn btn-sm btn-danger" title="Excluir registro" @click="excluir(item)"
                      v-if="acl.can('supervisor') || acl.can('admin')">
                      <i class="flaticon-delete"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="row mb-2">
          <div class="col-md-12">
            <p class="">Registros: {{ controle.itens.length }}</p>
          </div>
        </div>
        <div class="text-center">
          <span class="">{{ controle.status }}</span>
          <span class="text-danger fw-bold">{{ controle.erro }}</span>
        </div>
      </div>
    </div>
  </TrataTecla>

  <TrataTecla>
    <MsgBox :id="'janAddPlanoItem'" class="modal-md" data-bs-backdrop="static" :exibir="controle.janela"
      :titulo="'Cadastro de Item'" @on-fechou="controle.janela = false" :botao-gravar="true"
      :botao-fechar="true" @on-gravar="salvar()">
      <div class="row mb-2">
        <div class="col-md-3">
          Descrição
        </div>
        <div class="col-md-9">
          <TextBox v-model="controle.model.descricao" :formato="'Texto'" />
        </div>
      </div>

      <div class="row mb-2">
        <div class="col-md-3">
          Idade Mínima
        </div>
        <div class="col-md-4">
          <TextBox v-model="controle.model.idadeMinima" :formato="'Numero'" />
        </div>
      </div>

      <div class="row mb-2">
        <div class="col-md-3">
          Idade Máxima
        </div>
        <div class="col-md-4">
          <TextBox v-model="controle.model.idadeMaxima" :formato="'Numero'" />
        </div>
      </div>

      <div class="row mb-2">
        <div class="col-md-3">
          Valor R$
        </div>
        <div class="col-md-4">
          <TextBox v-model="controle.model.valor" :formato="'DoisDecimais'" class="text-end" />
        </div>
      </div>

      <div class="row mb-2">
        <div class="col-md-3">
          Com Seguro?
        </div>
        <div class="col-md-4">
          <DropdownSimNao v-model="controle.model.comSeguro" class="form-select form-select-sm" />
        </div>
      </div>

      <div class="row" v-if="controle.model.comSeguro">
        <div class="col-md-3">
          Valor Com Seguro R$
        </div>
        <div class="col-md-4">
          <TextBox v-model="controle.model.valorComSeguro" :formato="'DoisDecimais'" class="text-end" />
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <span class="">{{ controle.status }}</span>
          <span class="text-danger fw-bold">{{ controle.erro }}</span>
        </div>
      </div>

    </MsgBox>
  </TrataTecla>

  <MsgBox :id="'janPergunta'" class="modal-md" data-bs-backdrop="static" :exibir="controle.janPergunta"
    :titulo="'Atenção'" @on-fechou="controle.janPergunta = false" :botao-sim="true"
    :botao-nao="true" @on-sim="excluirConfirma()">
    <div class="row mb-2">
      <div class="col-md-12 fs-5 fw-bold text-center ">
        Deseja excluir este registro ?
      </div>
    </div>
  </MsgBox>

</template>
